/**
 * MODULE: platform/browser/frontend/components/BrowserAssistantComposer.tsx
 * PURPOSE: Browser command composer — matches shell AI assistant side-rail card styling.
 * RECENT: 2026-06-18 [2.2.1450] — Parity with **`AssistantPerplexityComposer`** + **`assistantPageTheme`** rail tokens.
 */

import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { StopCircleIcon } from '@heroicons/react/24/outline';
import type { RefObject } from 'react';
import {
  ASSISTANT_RAIL_COMPOSER_CLASS,
} from '@design-system/components/AiAssistant/assistantPageTheme';

export type BrowserAssistantComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  placeholder: string;
  onCancelStream?: () => void;
  inputRef?: RefObject<HTMLTextAreaElement>;
  /** Compact single-line dock under the live viewport. */
  compact?: boolean;
};

export function BrowserAssistantComposer({
  value,
  onChange,
  onSend,
  disabled = false,
  isStreaming = false,
  placeholder,
  onCancelStream,
  inputRef,
  compact = false,
}: BrowserAssistantComposerProps) {
  return (
    <div
      className={ASSISTANT_RAIL_COMPOSER_CLASS}
      data-testid={compact ? 'browser-command-dock-composer' : 'browser-assistant-composer'}
    >
      <div className={`px-4 ${compact ? 'py-2' : 'pt-3 pb-1'}`}>
        <textarea
          ref={inputRef}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          rows={compact ? 1 : 2}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          data-testid="browser-composer-input"
          className="min-h-[2.5rem] w-full resize-none border-0 bg-transparent text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-gray-500 disabled:opacity-50"
        />
      </div>
      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {isStreaming ? 'Agent is working in the remote browser…' : 'Remote browser automation'}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {isStreaming && onCancelStream ? (
            <button
              type="button"
              onClick={onCancelStream}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-600 text-white transition hover:bg-amber-700"
              aria-label="Cancel agent"
              data-testid="browser-cancel-agent-composer"
            >
              <StopCircleIcon className="h-5 w-5" />
            </button>
          ) : (
            <button
              type="button"
              disabled={disabled || !value.trim()}
              onClick={onSend}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-900 text-white transition hover:bg-gray-800 disabled:opacity-40 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              aria-label="Send"
              data-testid="browser-send-button"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
